/**
 * §G "Legacy mirror" — read-only migration bridge from the three legacy
 * brand_restrictions fields to typed bb_compliance_rules rows. See the
 * "Legacy mirror" group in
 * ai-docs/brand-bible-v2/design-artifacts/g-compliance-never-list/Section G
 * Design Spec.html §2, and boundaries §5.
 *
 * Split out of CompliancePanel.tsx purely for the 3,000-line file-size rule —
 * this is still §G's typed CompliancePanel, just extracted.
 *
 * services_not_offered and regulatory_notes each get a "migrate to a rule"
 * affordance that creates an excluded_service / regulated row respectively.
 * words_avoid is a READ-ONLY POINTER ONLY — those values belong to §B voice
 * never-say chunks, not §G (design spec §5: "words_avoid maps to §B
 * never-say chunks, not §G — the fold-in must not double-write it"). There is
 * deliberately no migrate action for it here.
 *
 * This component never creates a rule itself — `onMigrate` is the caller's
 * own addRule, so every migration goes through CompliancePanel's one create
 * path (same duplicate check, trim, and kind allowlist). `isMigrated` is the
 * caller's own duplicate predicate (ruleExists in CompliancePanel.tsx) so
 * "already migrated" uses the exact same kind+text comparison as the create
 * path's own dup guard, not a second implementation of it.
 */
import type { ComplianceKind } from '@/features/brand-bible-wizard/types';
import { StepCard } from '@/components/brand-bible-wizard/primitives';

export interface ComplianceLegacyInput {
  servicesNotOffered: string[];
  regulatoryNotes: string;
  wordsAvoid: string[];
}

function MigrateButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-none rounded-lg border border-dashed border-input bg-warm-bg px-2.5 py-1 text-[11px] font-medium text-teal hover:border-coral hover:text-coral-dark"
    >
      Migrate to a rule
    </button>
  );
}

function MigratedBadge() {
  return (
    <span className="flex-none rounded-[5px] bg-teal-light px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-teal-dark">
      Migrated
    </span>
  );
}

export function ComplianceLegacyMirror({
  legacy,
  isMigrated,
  onMigrate,
}: {
  legacy?: ComplianceLegacyInput;
  isMigrated: (kind: ComplianceKind, text: string) => boolean;
  onMigrate: (kind: ComplianceKind, text: string) => void;
}) {
  if (!legacy) return null;

  const services = legacy.servicesNotOffered.filter((s) => s.trim().length > 0);
  const notes = legacy.regulatoryNotes.trim();
  const words = legacy.wordsAvoid.filter((w) => w.trim().length > 0);

  if (services.length === 0 && !notes && words.length === 0) return null;

  return (
    <StepCard
      title="Legacy mirror"
      className="bg-teal-light"
      headerRight={<span className="text-xs text-muted-foreground">read-only</span>}
    >
      <div className="flex flex-col gap-3.5 p-4 text-muted-foreground">
        {services.length > 0 && (
          <div>
            <div className="mb-1.5 text-xs font-semibold text-teal-dark">Services not offered (legacy)</div>
            <ul className="flex flex-col gap-1.5">
              {services.map((text) => (
                <li key={text} className="flex items-center gap-2.5 text-[13.5px]">
                  <span className="flex-1">{text}</span>
                  {isMigrated('excluded_service', text) ? (
                    <MigratedBadge />
                  ) : (
                    <MigrateButton onClick={() => onMigrate('excluded_service', text)} />
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {notes && (
          <div>
            <div className="mb-1.5 text-xs font-semibold text-teal-dark">Regulatory notes (legacy)</div>
            <div className="flex items-start gap-2.5 text-[13.5px]">
              <span className="flex-1">{notes}</span>
              {isMigrated('regulated', notes) ? (
                <MigratedBadge />
              ) : (
                <MigrateButton onClick={() => onMigrate('regulated', notes)} />
              )}
            </div>
          </div>
        )}

        {words.length > 0 && (
          <div>
            <div className="mb-1.5 text-xs font-semibold text-teal-dark">Words to avoid (legacy)</div>
            <ul className="flex flex-wrap gap-2">
              {words.map((text) => (
                <li key={text} className="rounded-full border border-border bg-warm-bg px-3 py-1 text-xs">
                  {text}
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[11px]">These live in the Voice section, not here — no rule is created from them.</p>
          </div>
        )}
      </div>
    </StepCard>
  );
}
